import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../../store";
import { profileEditRequestThunk } from "../../../store/onboarding/thunk";
import {
  TRequestDetails,
  TUploadedDocument,
} from "../../../store/uploads/types";
import { createValidationSchema } from "../../../utils/createValidationSchema";
import SaveNotification from "../../SaveNotification";
import CustomButton from "../../UI/Buttons/CustomButton";
import RoundedTick from "../../UI/Icons/RoundedTick";
import Warning from "../../UI/Icons/Warning";
import DocumentUpload from "../../UI/Inputs/DocumentUpload";
import TextArea from "../../UI/Inputs/TextArea";
import PopupModal from "../../UI/Popup";
import ErrorMessage from "../../UI/Tooltips/ErrorMessage";
import { NOTIFICATION_TIME_OUT } from "../../../constants/app";

interface MakeARequestProps {
  showRequestModal: boolean;
  setShowRequestModal: (value: boolean) => void;
  reuestFrom: string;
}

const MakeARequest: React.FC<MakeARequestProps> = ({
  showRequestModal,
  setShowRequestModal,
  reuestFrom,
}) => {
  const inProgress = useAppSelector((state) => state.onboarding.inProgress);
  const [showNotification, setShowNotification] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<TUploadedDocument[]>([]);
  const [saveNotificationMsg, setSaveNotificationMsg] = useState("");
  const [makeARequestId, setMakeARequestId] = useState<number | string | null>(
    null
  );
  const [uploadedAttachments, setUploadedAttachments] = useState<
    TUploadedDocument[]
  >([]);
  const dispatch = useAppDispatch();

  /**
   * Creates a request ID based on the `reuestFrom` prop.
   * @returns string
   */
  const requestFrom = () => {
    if (reuestFrom === "personalDetails") {
      return "personal";
    } else if (reuestFrom === "qualifications") {
      return "qualifications";
    } else {
      return "";
    }
  };

  /**
   * Handles the file selection for the document upload.
   *
   * This function updates the uploaded files state and sets the request ID in the form values.
   * It also handles the deletion of files if `deleteFile` is true.
   *
   * @param file - The file object containing request details.
   * @param fileName - The name of the selected file.
   * @param deleteFile - A boolean indicating whether to delete the file (default is `false`).
   */
  const {
    handleSubmit,
    values,
    errors,
    touched,
    setFieldValue,
    handleChange,
    setFieldError,
  } = useFormik({
    validationSchema: createValidationSchema("profile_make_a_request"),
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: {
      details: "",
      request_id: makeARequestId,
      request_from: requestFrom(),
    },
    onSubmit: async () => {
      // Check if uploadedAttachments is empty, no need to pass it in the payload so added custom validation here
      if (uploadedAttachments.length === 0) {
        toast.error("Please upload at least one document.");
        return; // Prevent submission
      }

      // Prepare the payload without uploadedAttachments
      const payload = { ...values };
      try {
        const result = await dispatch(profileEditRequestThunk(payload));
        if (isFulfilled(result)) {
          const { data } = result.payload;
          if (data?.is_pending_request === true) {
            setSaveNotificationMsg(
              "You already have a pending request. Please wait for it to be processed."
            );
          } else {
            setSaveNotificationMsg("Submitted");
            setTimeout(() => {
              window.location.reload(); 
            }, NOTIFICATION_TIME_OUT); 
          }
          // toast.success("Your request has been submitted successfully.");
          setUploadedFiles([]); // Clear the uploaded files after successful submission
          setMakeARequestId(null); // Reset the request ID
          setFieldValue("request_id", null); // Reset the request ID in the form values
          setFieldError("request_id", ""); // Clear the error for request_id field
          setShowNotification(true);
          setShowRequestModal(false);
          setFieldValue("details", "");
          setFieldError("details", ""); // Clear the error for details field
        }
      } catch (error) {
        throw new Error(
          "Could not connect to services at the moment. Please try again. Thanks."
        );
      }
    },
  });

  /**
   * Handles the close action for the request modal.
   *
   * This function performs the following actions:
   * - Closes the request modal by setting `setShowRequestModal` to `false`.
   * - Resets the "details" field in the form values to an empty string.
   * - Resets the "request_id" field in the form values to an empty string.
   * - Clears any validation errors for the "request_id" field.
   * - Clears any validation errors for the "details" field.
   */
  const onClickClose = () => {
    setShowRequestModal(false);
    setFieldValue("details", ""); // Reset the details field when closing the modal
    setFieldValue("request_id", ""); // Reset the request ID in the form values
    setFieldError("request_id", ""); // Clear the error for request_id field
    setFieldError("details", ""); // Clear the error for details field
  };

  /**
   * Handles the selection of a file, updating the request ID and managing the uploaded attachments.
   *
   * @param file - The file object containing request details.
   * @param fileName - The name of the selected file.
   * @param deleteFile - A boolean indicating whether to delete the file (default is `false`).
   * @param index - The index of the file in the attachments array.
   *
   * This function performs the following actions:
   * - Sets the request ID from the file object to be used for the next attachment.
   * - Updates the form field value for "request_id".
   * - Modifies the `uploadedAttachments` state:
   *   - If `deleteFile` is `true`, removes the file at the specified index and filters out undefined values.
   *   - Otherwise, updates the file at the specified index with the corresponding document from the file object.
   */
  const handleFileSelect = (
    file: TRequestDetails,
    fileName: string,
    deleteFile: boolean = false,
    index: number
  ) => {
    setMakeARequestId(file?.request_id); // Set the request ID from the file object to pass next attachment
    setFieldValue("request_id", file?.request_id); // Set the request ID in the form values
    setUploadedAttachments((prevAttachments) => {
      const updatedAttachments = [...prevAttachments];
      if (deleteFile) {
        updatedAttachments.splice(index, 1); // Remove the file at the given index
        return updatedAttachments.filter(
          (attachment) => attachment !== undefined
        ); // Filter out undefined values
      } else {
        updatedAttachments[index] = file?.documents[index]; // Update the file at the given index
      }
      return updatedAttachments;
    });
  };

  /**
   * Effect to handle the case when no files are uploaded.
   *
   */
  useEffect(() => {
    const uploadedFiles = uploadedAttachments.filter(
      (attachment) => attachment !== undefined
    );
    if (
      uploadedFiles.length === 0 ||
      uploadedFiles.every((item) => item === undefined)
    ) {
      setMakeARequestId(""); // Reset the uploaded files state if no files are selected
      setFieldValue("request_id", ""); // Reset the request ID in the form values
    }
  }, [uploadedAttachments]);

  /**
   *
   * @returns JSX.Element
   * Renders additional attachment slots for file uploads dynamically based on the number of uploaded files.
   */
  const renderAdditionalAttachment = () => {
    return (
      uploadedFiles.length > 0 &&
      // Render additional upload slots dynamically
      uploadedFiles.map((documentList, index) => (
        <div
          key={`upload-${index}`}
          className="d-flex gap-10 align-items-center"
        >
          <DocumentUpload
            onMakeARequestFileSelect={(file, fileName, deleteFile) =>
              handleFileSelect(file, documentList?.file_name, false, index)
            }
            type={"make_a_request"}
            makeARequestId={makeARequestId}
            required={index === 0} // Only the first upload is required
            attachmentList={documentList}
          />
        </div>
      ))
    );
  };

  /**
   * Effect to clear the error for the request_id field when makeARequestId changes.
   *
   * This effect runs whenever the makeARequestId state changes. If makeARequestId is not null, undefined, or an empty string,
   * it clears the error for the request_id field in the form.
   */
  useEffect(() => {
    if (
      makeARequestId !== null ||
      makeARequestId !== undefined ||
      makeARequestId !== ""
    ) {
      setFieldError("request_id", ""); // Clear the error for request_id field
    }
  }, [makeARequestId]);

  return (
    <>
      {showNotification && (
        <SaveNotification
          message={saveNotificationMsg}
          onClose={() => setShowNotification(false)}
        />
      )}
      <PopupModal
        visible={showRequestModal}
        showCloseBtn={true}
        onClickClose={onClickClose}
        customWidth={650}
      >
        <div className="d-flex flex-column gap-20 fw-400 text-black">
          <div className="d-flex flex-column gap-15">
            <div className="fs-22">Request a Profile Edit</div>
            <div className="d-flex flex-column gap-10">
              <div className="fs-16">
                Please give detail on which locked details you would like to
                modify:
              </div>
              <TextArea
                field={"details"}
                label={""}
                placeholder={"Enter Here..."}
                onChange={handleChange}
                value={values?.details}
                error={errors.details as string}
                touched={touched.details! as boolean}
                
              />
            </div>
            <div className="d-flex flex-column gap-10">
              <div className="fs-16">
                Please upload any documents related to your request:
              </div>

              {/* Render DocumentUpload components dynamically */}
              {uploadedFiles.length === 0 && (
                // Render the first upload slot if no files are uploaded yet
                <div className="d-flex gap-10 align-items-center">
                  <DocumentUpload
                    onMakeARequestFileSelect={(file, fileName, deleteFile) =>
                      handleFileSelect(file, fileName, false, 0)
                    }
                    type={"make_a_request"}
                    makeARequestId={makeARequestId}
                    required={true} // First upload is required
                  />
                </div>
              )}
              {errors && <ErrorMessage message={errors.request_id as string} />}
              <div className="d-flex gap-10 align-items-center">
                {renderAdditionalAttachment()}
              </div>
            </div>
          </div>
          <div className="bg-teal-light px-15px py-10px radius-8px d-flex gap-6 align-items-center">
            <Warning />
            <div className="fs-13 fw-400 text-black lh-sm">
              Please allow up to 1 business day for one of our consultants to
              review and approve changes.
            </div>
          </div>
          <div className="d-flex justify-content-end">
            <CustomButton
              variant={"primary"}
              type="button"
              className="btn-theme bg-teal text-black"
              onClick={handleSubmit}
              isLoading={inProgress}
              contentClass="d-flex"
            >
              <RoundedTick color={inProgress ? "#aaaaaa" : "black"} />
              Make Request
            </CustomButton>
          </div>
        </div>
      </PopupModal>
    </>
  );
};

export default MakeARequest;
